import { UserCalendarStatus } from 'generated-graphql/types';
import { isRoundRobinMethod } from './isRoundRobinMethod';

export type EditableTemplate = {
  availabilityMethod?: string | null;
  organizerUserCalendar?: {
    name?: string | null;
    status?: string | null;
    // Absent means "not checked"; empty means "no longer linked to this organization".
    userCalendarOrganizations?: {
      items?: ({ id: string } | null)[] | null;
    } | null;
  } | null;
};

// A collective organizer can't be changed once the template exists, so one out
// of reach can be read but never saved. Round robin belongs to the Popp grant.
export const getTemplateEditBlockedReason = (
  template: EditableTemplate
): string | null => {
  if (isRoundRobinMethod(template.availabilityMethod)) {
    return null;
  }

  const organizer = template.organizerUserCalendar;

  // "Choose an organiser." can never be satisfied — it can't be set later.
  if (!organizer) {
    return "This template has no organiser, so it can't be edited.";
  }

  const who = organizer.name ?? 'The organiser';
  const links = organizer.userCalendarOrganizations;

  // Panel removal leaves calendar status untouched, so status can't detect it.
  if (links && (links.items ?? []).length === 0) {
    return `${who} is no longer on this meeting panel, so this template can't be edited.`;
  }

  // An invited calendar was never connected, so "can't be reached" is wrong.
  if (organizer.status === UserCalendarStatus.Invited) {
    return `${who} hasn't connected their calendar yet, so this template can't be edited.`;
  }

  if (organizer.status && organizer.status !== UserCalendarStatus.Connected) {
    return `Popp can't reach ${who}'s calendar, so this template can't be edited. Reconnect the calendar to continue.`;
  }

  return null;
};
